import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Statistic, Row, Col, Popconfirm, Select, Table, Tag, Alert, Space, Typography, Spin, message, Empty } from 'antd';
import { ThunderboltOutlined, RightOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import { useNavigate } from 'react-router-dom';
import { LabelingGuideButton } from '../components/LabelingGuide';

import {
  fetchAgentActiveLearningQueue,
  fetchAgentCalibration,
  fetchAgentConfusionMatrix,
  fetchAgentHealthSummary,
  fetchAgentTimeseries,
  fetchClassMixWoW,
  fetchColdClasses,
  fetchMyContribution,
  fetchRuleMix,
  fetchLabelCoverage,
  fetchOutcomeAccuracy,
  fetchTriageActivity,
  fetchClassMixTrend,
  fetchAnalyticsFreshness,
  fetchTimeToTriage,
  embedBackfill,
  seedAgent,
  type AgentActiveLearningItem,
  type AgentCalibrationBucket,
  type AgentConfusionCell,
  type AgentHealthSummary,
  type AgentTimeseriesRow,
  type ClassMixWoWRow,
  type ColdClassResponse,
  type MyContribution,
  type RuleMixResponse,
  type LabelCoverageResponse,
  type OutcomeAccuracyResponse,
  type TriageActivityResponse,
  type ClassMixTrendResponse,
  type AnalyticsFreshness,
  type TimeToTriageRow,
} from '../config/api';

const { Title, Text } = Typography;

const WINDOW_OPTIONS = [
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
];

function pct(num: number, denom: number): string {
  if (!denom) return '—';
  return `${((num / denom) * 100).toFixed(1)}%`;
}

// Plain-language interpretation for each metric. Tag color + verdict + what
// to do. Keeps the "what does this mean" off-screen-explanation off-screen.
type Verdict = { color: 'success' | 'warning' | 'error' | 'default'; label: string; action: string };

function interpretBrier(b: number | null | undefined): Verdict {
  if (b == null) return { color: 'default', label: 'no labels yet', action: 'close issues to populate' };
  const v = Number(b);
  if (v < 0.10) return { color: 'success', label: 'well-calibrated', action: 'confidence numbers are decision-grade' };
  if (v < 0.20) return { color: 'warning', label: 'watch', action: 'monitor; add more labels' };
  return { color: 'error', label: 'unreliable', action: 'ignore confidence values for now' };
}

function interpretAgreement(agree: number, total: number): Verdict {
  if (!total) return { color: 'default', label: 'no labels yet', action: 'close issues to populate' };
  const r = agree / total;
  if (r >= 0.85) return { color: 'success', label: 'high', action: 'consider Phase 2 once 100+ labels' };
  if (r >= 0.70) return { color: 'warning', label: 'mixed', action: 'inspect confusion matrix' };
  return { color: 'error', label: 'low', action: 'agent disagrees often — investigate' };
}

function interpretLabelVolume(n: number): Verdict {
  if (n === 0) return { color: 'error', label: 'none', action: 'agent runs zero-shot until labels arrive' };
  if (n < 50) return { color: 'warning', label: 'sparse', action: `need ${50 - n} more for a basic signal` };
  if (n < 100) return { color: 'warning', label: 'fair', action: 'metrics meaningful but noisy' };
  return { color: 'success', label: 'good', action: 'metrics meaningful' };
}

const TriageAgentHealth: React.FC = () => {
  const navigate = useNavigate();
  const [windowDays, setWindowDays] = useState<number>(30);
  const [summary, setSummary] = useState<AgentHealthSummary | null>(null);
  const [matrix, setMatrix] = useState<AgentConfusionCell[]>([]);
  const [calibration, setCalibration] = useState<AgentCalibrationBucket[]>([]);
  const [timeseries, setTimeseries] = useState<AgentTimeseriesRow[]>([]);
  const [queue, setQueue] = useState<AgentActiveLearningItem[]>([]);
  const [freshness, setFreshness] = useState<AnalyticsFreshness | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [seeding, setSeeding] = useState<boolean>(false);
  const [embedBackfilling, setEmbedBackfilling] = useState<boolean>(false);
  const [reloadTick, setReloadTick] = useState<number>(0);
  const [contribution, setContribution] = useState<MyContribution | null>(null);
  const [coldClasses, setColdClasses] = useState<ColdClassResponse | null>(null);
  // UX-016: new metrics surfaces. Server endpoints shipped in PR7 but weren't
  // consumed by the UI. time-to-triage is per-day, so we summarize across the
  // last N days (last day with data is the "current" tile). class-mix-wow
  // shows top-5 movers as a side panel next to the daily class-mix chart.
  const [timeToTriage, setTimeToTriage] = useState<TimeToTriageRow[]>([]);
  const [classMixWoW, setClassMixWoW] = useState<ClassMixWoWRow[]>([]);
  // Rule-mix observability: rule sources, class distribution, sub-class
  // residual. Lets the admin verify post-deploy what the new rule chain
  // is producing. Reuses the windowDays selector.
  const [ruleMix, setRuleMix] = useState<RuleMixResponse | null>(null);
  // Label coverage — operator-labeled vs auto-closed share + autoclose-rate by
  // class. Makes the operator-label bias in the accuracy metrics visible.
  const [labelCoverage, setLabelCoverage] = useState<LabelCoverageResponse | null>(null);
  // Heuristic monitoring: outcome-accuracy back-test, operator-triage activity,
  // and class-mix drift.
  const [outcomeAccuracy, setOutcomeAccuracy] = useState<OutcomeAccuracyResponse | null>(null);
  const [triageActivity, setTriageActivity] = useState<TriageActivityResponse | null>(null);
  const [classMixTrend, setClassMixTrend] = useState<ClassMixTrendResponse | null>(null);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const r = await seedAgent(50);
      message.success(`Seeded ${r.dispatched} issues — predictions land over the next minutes.`);
      setTimeout(() => setReloadTick((n) => n + 1), 30_000);
    } catch (e: any) {
      message.error(`Seed failed: ${e.message}`);
    } finally {
      setSeeding(false);
    }
  };

  const handleEmbedBackfill = async () => {
    setEmbedBackfilling(true);
    try {
      const r = await embedBackfill(200);
      message.success(`Embed-backfilling ${r.dispatched} labeled issues. ${r.remaining_eligible} more eligible.`);
      // Match handleSeed: trigger a refetch after the sidecar has had time to
      // process. Without this, the page stayed visually stale even though
      // embeddings were landing in DB — operator couldn't tell if it worked.
      setTimeout(() => setReloadTick((n) => n + 1), 30_000);
    } catch (e: any) {
      message.error(`Embed-backfill failed: ${e.message}`);
    } finally {
      setEmbedBackfilling(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      fetchAgentHealthSummary(windowDays),
      fetchAgentConfusionMatrix(windowDays),
      fetchAgentCalibration(windowDays),
      fetchAgentTimeseries(windowDays),
      fetchAgentActiveLearningQueue(20),
      fetchAnalyticsFreshness(),
      fetchMyContribution().catch(() => null),
      // UX-016: new endpoints. Both tolerate failure individually so a
      // partial outage doesn't kill the whole page.
      fetchTimeToTriage(windowDays).catch(() => ({ since_days: windowDays, rows: [] })),
      fetchClassMixWoW().catch(() => ({ rows: [] })),
      // Cold-class status — tolerate sidecar unreachable (503) or proxy
      // 502 so the rest of the page still renders. Null shows a "panel
      // unavailable" hint instead of breaking the load.
      fetchColdClasses().catch(() => null),
      fetchRuleMix(windowDays).catch(() => null),
      fetchLabelCoverage(windowDays).catch(() => null),
      fetchOutcomeAccuracy(Math.max(windowDays, 90), 21).catch(() => null),
      fetchTriageActivity(windowDays).catch(() => null),
      fetchClassMixTrend(8).catch(() => null),
    ])
      .then(([s, c, cal, ts, q, fr, my, ttt, cmw, cc, rmx, lc, oa, ta, cmt]) => {
        if (cancelled) return;
        setSummary(s);
        setMatrix(c.cells);
        setCalibration(cal.buckets);
        setTimeseries(ts.rows);
        setQueue(q.items);
        setFreshness(fr);
        setContribution(my);
        setTimeToTriage(ttt.rows);
        setClassMixWoW(cmw.rows);
        setColdClasses(cc);
        setRuleMix(rmx);
        setLabelCoverage(lc);
        setOutcomeAccuracy(oa);
        setTriageActivity(ta);
        setClassMixTrend(cmt);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(e.message);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [windowDays, reloadTick]);

  // Refetch when the tab regains focus. The Active Learning queue gets
  // stale the moment the operator clicks Triage and goes to label an
  // issue — when they tab back, the just-labeled row should be gone.
  // visibilitychange covers tab-switching within the same window;
  // window 'focus' covers alt-tabbing or returning from another app.
  useEffect(() => {
    const onWake = () => {
      if (document.visibilityState === 'visible') {
        setReloadTick((n) => n + 1);
      }
    };
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);
    return () => {
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
    };
  }, []);

  // ---- Time-to-triage rollup ----
  // Server returns one row per day. Surface as a single "latest day" tile +
  // average across the window. p50 is what the operator should expect;
  // p95 is the worst-day tail that hides under the median.
  const timeToTriageSummary = useMemo(() => {
    if (timeToTriage.length === 0) return null;
    const latest = timeToTriage[timeToTriage.length - 1];
    const fmt = (ms: number | null) => {
      if (ms == null) return '—';
      const sec = ms / 1000;
      if (sec < 60) return `${sec.toFixed(0)}s`;
      if (sec < 3600) return `${(sec / 60).toFixed(1)}m`;
      return `${(sec / 3600).toFixed(1)}h`;
    };
    const totalLabels = timeToTriage.reduce((acc, r) => acc + (r.n || 0), 0);
    return {
      latest_day: latest.day,
      latest_n: latest.n,
      latest_p50: fmt(latest.p50_ms),
      latest_p95: fmt(latest.p95_ms),
      window_labels: totalLabels,
    };
  }, [timeToTriage]);

  // ---- Class-mix WoW top movers ----
  // Sort by abs(delta_n) and surface the top 5. delta_pct is sometimes null
  // (when last_week_n=0); we tag those as "new" rather than ∞%.
  const wowTopMovers = useMemo(() => {
    if (classMixWoW.length === 0) return [];
    return [...classMixWoW]
      .sort((a, b) => Math.abs(b.delta_n) - Math.abs(a.delta_n))
      .slice(0, 5);
  }, [classMixWoW]);

  // ---- Confusion matrix -> echarts heatmap ----
  const matrixChart = useMemo(() => {
    if (matrix.length === 0) return null;
    const predicted = Array.from(new Set(matrix.map((c) => c.predicted_name)));
    const truth = Array.from(new Set(matrix.map((c) => c.truth_name)));
    const data = matrix.map((c) => [
      truth.indexOf(c.truth_name),
      predicted.indexOf(c.predicted_name),
      c.n,
    ]);
    const max = Math.max(...matrix.map((c) => c.n), 1);
    return {
      tooltip: {
        position: 'top',
        formatter: (p: any) => {
          const [tIdx, pIdx, n] = p.value;
          return `predicted: <b>${predicted[pIdx]}</b><br/>actual: <b>${truth[tIdx]}</b><br/>count: ${n}`;
        },
      },
      // UX-035: previously the axes were just "operator label" / "agent
      // prediction" — non-sklearn convention (sklearn puts truth on rows,
      // predicted on columns) without explanation. Labels now spell out
      // what each axis means and where to read accuracy from.
      grid: { height: '70%', top: '14%' },
      xAxis: {
        type: 'category',
        data: truth,
        name: 'operator label (truth)',
        nameLocation: 'middle',
        nameGap: 30,
        splitArea: { show: true },
      },
      yAxis: {
        type: 'category',
        data: predicted,
        name: 'agent prediction',
        nameLocation: 'middle',
        nameGap: 60,
        splitArea: { show: true },
      },
      visualMap: { min: 0, max, calculable: true, orient: 'horizontal', left: 'center', bottom: 0 },
      series: [
        {
          name: 'count',
          type: 'heatmap',
          data,
          label: { show: true },
          emphasis: { itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,0.5)' } },
        },
      ],
    };
  }, [matrix]);

  // ---- Calibration plot: bucket midpoint vs agreement rate ----
  const calibChart = useMemo(() => {
    if (calibration.length === 0) return null;
    const points = calibration.map((b) => [(b.bucket * 10 + 5) / 100, Number(b.agreement_rate)]);
    const counts = calibration.map((b) => [(b.bucket * 10 + 5) / 100, b.n]);
    return {
      tooltip: { trigger: 'axis' },
      legend: { data: ['agreement', 'predictions'] },
      xAxis: { type: 'value', min: 0, max: 1, name: 'confidence' },
      yAxis: [
        { type: 'value', min: 0, max: 1, name: 'agreement', position: 'left' },
        { type: 'value', name: 'count', position: 'right' },
      ],
      series: [
        {
          name: 'agreement',
          // Scatter, not line — the points are independent bucket measurements,
          // not a continuous series. The interpolated line implied a trend
          // between buckets that doesn't exist.
          type: 'scatter',
          data: points,
          symbolSize: 10,
        },
        {
          name: 'reference',
          type: 'line',
          data: [
            [0, 0],
            [1, 1],
          ],
          lineStyle: { type: 'dashed' },
          symbol: 'none',
          tooltip: { show: false },
        },
        {
          name: 'predictions',
          type: 'bar',
          yAxisIndex: 1,
          barWidth: 8,
          itemStyle: { opacity: 0.4 },
          data: counts,
        },
      ],
    };
  }, [calibration]);

  // ---- Class-mix timeseries: daily counts per class ----
  const mixChart = useMemo(() => {
    if (timeseries.length === 0) return null;
    const dates = Array.from(new Set(timeseries.map((r) => r.metric_date))).sort();
    const classes = Array.from(new Set(timeseries.map((r) => r.ticket_class)));
    const series = classes.map((cls) => ({
      name: cls,
      type: 'line',
      stack: 'predictions',
      areaStyle: {},
      emphasis: { focus: 'series' },
      data: dates.map((d) => {
        const row = timeseries.find((r) => r.metric_date === d && r.ticket_class === cls);
        return row?.total_predictions ?? 0;
      }),
    }));
    return {
      tooltip: { trigger: 'axis' },
      legend: { data: classes },
      xAxis: { type: 'category', data: dates, boundaryGap: false },
      yAxis: { type: 'value', name: 'predictions/day' },
      series,
    };
  }, [timeseries]);

  // ---- Active learning table ----
  const queueColumns = [
    {
      title: 'Issue',
      dataIndex: 'issue_id',
      width: 200,
      render: (id: number, row: AgentActiveLearningItem) => (
        // 2026-05-26 audit: was an <a href> which triggered a full page
        // reload (and Auth0 session re-init). Same row also has a Triage
        // button below that uses SPA nav — making them consistent.
        <Typography.Link onClick={() => navigate(`/triage?issue=${id}`)}>
          #{id} {row.observable_name || row.observable_id}
        </Typography.Link>
      ),
    },
    { title: 'Room/Key', dataIndex: 'key_name', width: 140, render: (v: string | null) => v || '—' },
    {
      // "Why is this in the queue" — without this, operators have to
      // visually diff rule_class vs agent_class to figure out whether the
      // row was surfaced because of disagreement or as a low-confidence
      // fallback (PR3 added that path).
      title: 'Why',
      width: 130,
      render: (_: unknown, row: AgentActiveLearningItem) => {
        if (row.class10_locked) {
          return <Tag color="orange">class-10 lock</Tag>;
        }
        if (row.disagreement_flag) {
          return <Tag color="gold">disagrees</Tag>;
        }
        return <Tag>low confidence</Tag>;
      },
    },
    {
      title: 'Rule says',
      width: 180,
      render: (_: unknown, row: AgentActiveLearningItem) => (
        <Space direction="vertical" size={0}>
          <Tag>{row.rule_class || '—'}</Tag>
          <Text type="secondary">{row.rule_confidence ?? '—'}%</Text>
        </Space>
      ),
    },
    {
      title: 'Agent says',
      width: 180,
      render: (_: unknown, row: AgentActiveLearningItem) => {
        // agent_class is the raw LLM verdict. When the surfaced (post-ensemble)
        // class differs, the agent was shadowed back to the rule — note it so
        // operators know the suggestion was logged but not acted on.
        const shadowed =
          row.surfaced_class &&
          row.surfaced_class_id !== row.agent_class_id;
        return (
          <Space direction="vertical" size={0}>
            <Tag color="blue">{row.agent_class}</Tag>
            <Text type="secondary">{row.agent_confidence}%</Text>
            {shadowed && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                shadowed → {row.surfaced_class}
              </Text>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Reasoning',
      dataIndex: 'reasoning',
      render: (v: string | null) => (
        <Text style={{ whiteSpace: 'pre-wrap' }}>{v || '—'}</Text>
      ),
    },
    {
      title: 'Action',
      width: 120,
      render: (_: unknown, row: AgentActiveLearningItem) => (
        <Button
          size="small"
          type="primary"
          icon={<RightOutlined />}
          onClick={() => navigate(`/triage?issue=${row.issue_id}`)}
        >
          Triage
        </Button>
      ),
    },
  ];

  const expandedRowRender = (row: AgentActiveLearningItem) => {
    const alts = Array.isArray(row.top_alternatives) ? row.top_alternatives : [];
    return (
      <div style={{ padding: '8px 16px', background: '#fafafa' }}>
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          {alts.length > 0 && (
            <div>
              <Text strong>Agent's other guesses: </Text>
              {alts.map((a, i) => (
                <Tag key={i}>cls {a.class_id} ({a.confidence}%)</Tag>
              ))}
            </div>
          )}
          <div>
            <Text type="secondary">
              predicted at {new Date(row.predicted_at).toLocaleString()} · observable {row.observable_id}
            </Text>
          </div>
        </Space>
      </div>
    );
  };

  // Page-level status: top-of-page summary that tells the operator what
  // matters right now and what to do next. Order matters — most blocking
  // condition first.
  const pageStatus = useMemo(() => {
    const items: { type: 'error' | 'warning' | 'info' | 'success'; title: string; body: string }[] = [];
    // Use end_of_day_lag_minutes when available (correct day-granularity
    // calc); fall back to legacy lag_minutes for older server builds.
    const freshnessLagMin =
      freshness?.end_of_day_lag_minutes ?? freshness?.lag_minutes ?? null;
    if (freshness && freshnessLagMin != null && Number(freshnessLagMin) / (60 * 24) > 3) {
      items.push({
        type: 'error',
        title: 'Upstream feed broken — agent comparisons are unreliable',
        body: 'sensorflow_analytics hasn\'t refreshed; the rule baseline is degraded. Don\'t draw conclusions about agent quality from this window. Ping whoever owns the sensorflow_analytics refresh job before acting on anything below.',
      });
    }
    if (!summary || summary.total_predictions === 0) {
      items.push({
        type: 'info',
        title: 'No agent predictions yet',
        body: 'Click "Seed agent (50)" to populate quickly, or wait for the next sweep cycle (every 15 min) to bring in real traffic.',
      });
    } else if ((summary.with_label || 0) === 0) {
      items.push({
        type: 'info',
        title: 'No operator labels yet — agent is running zero-shot',
        body: 'Until you close some issues with classes set, the agent has no exemplars to learn from and the accuracy charts stay empty. Each close adds a labeled row.',
      });
    } else if ((summary.with_label || 0) < 50) {
      items.push({
        type: 'warning',
        title: `Only ${summary.with_label} operator labels collected`,
        body: 'Need at least 50 to read agreement and 100+ to trust hold-out. Charts are populated but figures will move sharply with each new label.',
      });
    } else if (summary.brier_score != null && Number(summary.brier_score) < 0.10 && summary.holdout_total >= 10) {
      items.push({
        type: 'success',
        title: 'Agent metrics look healthy',
        body: 'Calibration and hold-out agreement are within Phase-2 thresholds. Ready to discuss flipping out of SHADOW_MODE.',
      });
    }
    return items;
  }, [freshness, summary]);

  const freshnessAlert = (() => {
    if (!freshness) return null;
    const lagMin = Number(
      freshness.end_of_day_lag_minutes ?? freshness.lag_minutes ?? Infinity,
    );
    const lagDays = lagMin / (60 * 24);
    if (lagDays > 3) {
      return (
        <Alert
          type="error"
          showIcon
          message={`sensorflow_analytics stale — latest event ${lagDays.toFixed(1)}d ago`}
          description={
            <Space direction="vertical" size={4}>
              <Text>
                The investigation feed (sensorflow_analytics.v_node_offline_investigation) hasn't
                refreshed in {lagDays.toFixed(1)} days. The classifier auto-drops
                sensorflow_analytics-derived rules when a node's sensorflow_analytics row is &gt; 3 days
                behind its actual offline state (defense-in-depth), so verdicts
                degrade to non-sensorflow_analytics paths (gateway-direct, OTA, test-jig,
                R-TAMPER-SUDDEN, cohort, fallback to OFFLINE_UNKNOWN).
              </Text>
              <Text style={{ fontSize: 12, color: '#595959' }}>
                Root cause is upstream — the dbt model exists and runs, but its
                scheduler stalled. Has to be re-enabled by the analytics team.
                Until then: confidence on rule verdicts is bounded by available
                non-sensorflow_analytics signals; the agent's LLM verdict (when present)
                operates on the SAME stale source so don't over-rely on it
                either.
              </Text>
            </Space>
          }
        />
      );
    }
    if (lagDays > 1) {
      return (
        <Alert
          type="warning"
          showIcon
          message={`sensorflow_analytics lagging — latest event ${lagDays.toFixed(1)}d ago`}
        />
      );
    }
    return null;
  })();

  return (
    // TriageLayout pins the outer chrome at height:100vh with overflow:hidden
    // (the /triage 3-pane layout manages its own scroll inside that). For
    // long-content routes like Agent Health we need an explicit scrollable
    // wrapper, otherwise content below the fold is just clipped.
    <div style={{ height: '100%', overflow: 'auto' }}>
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Triage Agent Health</Title>
        <Space>
          {/* Both buttons dispatch real LLM/embedding calls — ~$0.02-0.05
              per seed (50 issues × Claude Haiku) and ~$0.002 per embed (200
              issues × OpenAI text-embedding-3-small). Popconfirm guards
              against accidental clicks. */}
          <Popconfirm
            title="Seed the agent with 50 issues?"
            description="Fires ~50 Claude Haiku /classify calls (~$0.02-0.05). Tier 1 rate limits may slow it down."
            okText="Seed"
            cancelText="Cancel"
            onConfirm={handleSeed}
          >
            <Button icon={<ThunderboltOutlined />} loading={seeding}>
              Seed agent (50)
            </Button>
          </Popconfirm>
          <Popconfirm
            title="Embed-backfill 200 labeled issues?"
            description="Fires ~200 OpenAI embedding calls (~$0.002). No LLM, no rate-limit risk."
            okText="Backfill"
            cancelText="Cancel"
            onConfirm={handleEmbedBackfill}
          >
            <Button loading={embedBackfilling}>
              Embed-backfill labeled (200)
            </Button>
          </Popconfirm>
          <Select
            value={windowDays}
            onChange={setWindowDays}
            options={WINDOW_OPTIONS}
            style={{ width: 180 }}
          />
        </Space>
      </Space>

      {/* Contribution banner — two flavors: a celebratory tally when the
          operator has contributed something, and a softer call-to-action
          when they haven't. The original "Your labels train the agent · 0
          today · 0 this week · 0 total" read as patronizing at zero counts. */}
      {contribution && contribution.closes_total > 0 && (
        <Alert
          type={contribution.closes_today > 0 ? 'success' : 'info'}
          showIcon
          style={{ marginBottom: 16 }}
          message={
            <Space size={16} wrap>
              <Text strong>Your labels train the agent.</Text>
              <Text>{contribution.closes_today} today</Text>
              <Text type="secondary">·</Text>
              <Text>{contribution.closes_this_week} this week</Text>
              <Text type="secondary">·</Text>
              <Text>{contribution.closes_total} total</Text>
              <Text type="secondary">·</Text>
              <Text>{contribution.classes_distinct} distinct classes</Text>
              <LabelingGuideButton size="small" label="Guide" />
            </Space>
          }
        />
      )}
      {contribution && contribution.closes_total === 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="No operator labels yet — the agent is running blind"
          description={
            <Space direction="vertical" size={4}>
              <Text>
                Close issues with a class set and you'll start training the
                agent. Every accuracy metric below is currently computed against
                rule-derived "inferred" labels, which is self-validation — not
                truth.
              </Text>
              <Space size={12} wrap>
                <LabelingGuideButton size="small" label="Read the labeling guide" />
                <Text style={{ fontSize: 12, color: '#595959' }}>
                  First milestone: 50 labels. Phase-2 unlock: 100 labels + hold-out ≥ 70%.
                </Text>
              </Space>
            </Space>
          }
        />
      )}

      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}
      {freshnessAlert && <div style={{ marginBottom: 16 }}>{freshnessAlert}</div>}

      {pageStatus.length > 0 && (
        <Space direction="vertical" size={8} style={{ width: '100%', marginBottom: 16 }}>
          {pageStatus.map((s, i) => (
            <Alert
              key={i}
              type={s.type}
              showIcon
              message={<Text strong>{s.title}</Text>}
              description={s.body}
            />
          ))}
        </Space>
      )}

      <Spin spinning={loading}>
        {summary && (() => {
          const labelV = interpretLabelVolume(summary.with_label || 0);
          const agreeV = interpretAgreement(summary.agree_count || 0, summary.with_label || 0);
          const holdoutV = interpretAgreement(summary.holdout_agree || 0, summary.holdout_total || 0);
          const brierV = interpretBrier(summary.brier_score);
          return (
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={8} md={6} lg={4}>
                <Card>
                  <Statistic title="Predictions" value={summary.total_predictions} />
                  <Text style={{ fontSize: 12, color: '#595959' }}>agent verdicts logged in window</Text>
                </Card>
              </Col>
              <Col xs={24} sm={8} md={6} lg={4}>
                <Card>
                  <Statistic
                    title="Operator-labeled"
                    value={summary.with_label}
                    suffix={summary.total_predictions ? `(${pct(summary.with_label, summary.total_predictions)})` : ''}
                  />
                  <Tag color={labelV.color}>{labelV.label}</Tag>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>{labelV.action}</Text>
                </Card>
              </Col>
              <Col xs={24} sm={8} md={6} lg={4}>
                <Card>
                  <Statistic
                    title="Agreement (rule-vs-operator)"
                    value={summary.with_label ? `${(summary.agree_count / summary.with_label * 100).toFixed(1)}%` : '—'}
                  />
                  <Tag color={agreeV.color}>{agreeV.label}</Tag>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>{agreeV.action}</Text>
                </Card>
              </Col>
              <Col xs={24} sm={8} md={6} lg={4}>
                <Card>
                  <Statistic
                    title="Hold-out agreement"
                    value={summary.holdout_total ? `${(summary.holdout_agree / summary.holdout_total * 100).toFixed(1)}%` : '—'}
                  />
                  <Tag color={holdoutV.color}>{holdoutV.label}</Tag>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>only un-biased accuracy signal</Text>
                </Card>
              </Col>
              <Col xs={24} sm={8} md={6} lg={4}>
                <Card>
                  <Statistic
                    title="Avg confidence"
                    // Server emits 0-100 (smallint, CHECK 0..100). Suffix '%'
                    // is honest; '/100' was ambiguous (fraction or percent?).
                    value={summary.avg_confidence != null ? Number(summary.avg_confidence).toFixed(1) : '—'}
                    suffix={summary.avg_confidence != null ? '%' : undefined}
                  />
                  <Text style={{ fontSize: 12, color: '#595959' }}>compare with agreement to spot over-confidence</Text>
                </Card>
              </Col>
              <Col xs={24} sm={8} md={6} lg={4}>
                <Card>
                  <Statistic
                    // PR1 audit found the server computes top-class Brier
                    // only (squared error of confidence vs 0/1-correct of the
                    // PREDICTED class) — not the canonical multi-class Brier.
                    // Label accordingly so operators reading "well-calibrated"
                    // know what was measured.
                    title="Brier (top-class)"
                    value={summary.brier_score != null ? Number(summary.brier_score).toFixed(4) : '—'}
                  />
                  <Tag color={brierV.color}>{brierV.label}</Tag>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>{brierV.action}</Text>
                </Card>
              </Col>
              {/* UX-016: time-to-triage tile. Shows last-day p50 and the
                  tail (p95) so operators can spot a day where a long-running
                  issue dragged the queue. decision_latency_ms was junk before
                  PR3's first-seen ref fix, so values prior to ~2026-05-18 are
                  not comparable — show a footer hint to that effect. */}
              <Col xs={24} sm={8} md={6} lg={4}>
                <Card>
                  <Statistic
                    title="Time-to-triage (p50)"
                    value={timeToTriageSummary?.latest_p50 ?? '—'}
                  />
                  <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                    p95 {timeToTriageSummary?.latest_p95 ?? '—'} · {timeToTriageSummary?.latest_n ?? 0} closes
                  </Text>
                </Card>
              </Col>
            </Row>
          );
        })()}

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card
              title="Confusion matrix"
              extra={<Text style={{ fontSize: 12, color: '#595959' }}>diagonal = agent matched your label</Text>}
            >
              {matrixChart && (summary?.with_label ?? 0) >= 10
                ? <ReactECharts option={matrixChart} style={{ height: 380 }} notMerge={true} lazyUpdate={true} />
                : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <Space direction="vertical" size={4} align="center">
                        <Text>
                          {summary?.with_label
                            ? `Only ${summary.with_label} operator labels — matrix won't be readable until ≥10.`
                            : 'Nothing to show — needs operator labels.'}
                        </Text>
                        <Text style={{ fontSize: 12, color: '#595959' }}>Close issues with a class set; each row that lands here shows where the agent agreed or disagreed with you.</Text>
                      </Space>
                    }
                  />
                )}
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card
              title="Calibration"
              extra={<Text style={{ fontSize: 12, color: '#595959' }}>line should hug the dashed diagonal</Text>}
            >
              {calibChart
                ? <ReactECharts option={calibChart} style={{ height: 380 }} notMerge={true} lazyUpdate={true} />
                : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <Space direction="vertical" size={4} align="center">
                        <Text>Nothing to show — needs operator labels.</Text>
                        <Text style={{ fontSize: 12, color: '#595959' }}>Bars far from the diagonal mean the agent's confidence numbers don't reflect how often it's actually right.</Text>
                      </Space>
                    }
                  />
                )}
            </Card>
          </Col>
          <Col xs={24} lg={16}>
            <Card
              title="Daily class mix"
              extra={<Text style={{ fontSize: 12, color: '#595959' }}>sudden swings usually = upstream data change, not a real fleet shift</Text>}
            >
              {mixChart
                ? <ReactECharts option={mixChart} style={{ height: 320 }} notMerge={true} lazyUpdate={true} />
                : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="Daily aggregator hasn't run yet. Comes online at 02:05 UTC, or after the first sweep produces predictions."
                  />
                )}
            </Card>
          </Col>
          {/* UX-016: top WoW movers panel sits next to the class-mix chart
              on wide layouts (stacks below on narrow). Operator gets an
              at-a-glance "what's changing" without staring at the stacked
              area chart for differences week-on-week. */}
          <Col xs={24} lg={8}>
            <Card
              title="Top week-over-week movers"
              extra={<Text style={{ fontSize: 12, color: '#595959' }}>operator labels only</Text>}
            >
              {wowTopMovers.length === 0 ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No operator labels in the last 14 days yet."
                />
              ) : (
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                  {wowTopMovers.map((row) => {
                    const dir = row.delta_n > 0 ? 'up' : row.delta_n < 0 ? 'down' : 'flat';
                    const color = dir === 'up' ? 'red' : dir === 'down' ? 'green' : 'default';
                    const sign = row.delta_n > 0 ? '+' : '';
                    return (
                      <div
                        key={row.ticket_class_id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        <Text style={{ fontSize: 13 }}>{row.ticket_class}</Text>
                        <Space size={4}>
                          <Tag color={color} style={{ marginRight: 0 }}>
                            {sign}{row.delta_n}
                            {row.delta_pct != null && ` (${sign}${row.delta_pct}%)`}
                            {row.delta_pct == null && row.this_week_n > 0 && ' new'}
                          </Tag>
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            {row.last_week_n} → {row.this_week_n}
                          </Text>
                        </Space>
                      </div>
                    );
                  })}
                </Space>
              )}
            </Card>
          </Col>
          <Col xs={24}>
            <Card
              title="Cold-class status"
              extra={
                <Text style={{ fontSize: 12, color: '#595959' }}>
                  classes with &lt;{coldClasses?.threshold ?? 20} operator labels remain on the LLM-only path —
                  ensemble lets the agent win even at lower confidence
                </Text>
              }
            >
              {coldClasses == null ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Sidecar unreachable — cold-class data unavailable. Check that triage-agent is up."
                />
              ) : (
                <>
                  <div style={{ marginBottom: 12, fontSize: 12, color: '#8c8c8c' }}>
                    Last refresh:{' '}
                    {coldClasses.last_refresh_at
                      ? new Date(coldClasses.last_refresh_at).toLocaleString()
                      : 'never'}
                    . Auto-refresh every {Math.round(coldClasses.refresh_interval_seconds / 3600)}h.
                  </div>
                  <Table
                    size="small"
                    rowKey="class_id"
                    pagination={false}
                    dataSource={coldClasses.per_class}
                    columns={[
                      { title: 'ID',   dataIndex: 'class_id', width: 60, align: 'right' as const },
                      { title: 'Class', dataIndex: 'name', render: (v: string) => <Text code style={{ fontSize: 12 }}>{v.toLowerCase()}</Text> },
                      {
                        title: 'Operator labels',
                        dataIndex: 'operator_labels',
                        width: 140,
                        align: 'right' as const,
                        render: (n: number) => <Text style={{ fontFamily: 'monospace' }}>{n}</Text>,
                      },
                      {
                        title: 'Status',
                        dataIndex: 'is_cold',
                        width: 90,
                        render: (cold: boolean) =>
                          cold ? <Tag color="blue">cold</Tag> : <Tag color="green">warm</Tag>,
                      },
                      {
                        title: 'To threshold',
                        dataIndex: 'to_threshold',
                        width: 120,
                        align: 'right' as const,
                        render: (n: number, row: any) =>
                          row.is_cold ? (
                            <Text style={{ fontFamily: 'monospace', color: '#fa8c16' }}>
                              {n} more
                            </Text>
                          ) : (
                            <Text type="secondary">—</Text>
                          ),
                      },
                    ]}
                  />
                </>
              )}
            </Card>
          </Col>
          <Col xs={24}>
            <Card
              title="What to triage next"
              extra={<Text style={{ fontSize: 12, color: '#595959' }}>open issues where agent disagrees with rule — your label here trains the agent fastest</Text>}
            >
              <Table
                size="small"
                rowKey="issue_id"
                columns={queueColumns}
                dataSource={queue}
                pagination={false}
                scroll={{ x: 'max-content' }}
                expandable={{ expandedRowRender }}
                locale={{
                  emptyText: 'No open disagreements right now. Either nothing classified yet, or the agent agrees with the rule on every open issue.',
                }}
              />
            </Card>
          </Col>
        </Row>

        {/* Outcome accuracy: label-free heuristic-accuracy back-test. For settled
            predictions, did the node's actual later behavior match the predicted
            cause? 'down' classes (hardware/EOL) recovering = likely wrong. */}
        {outcomeAccuracy && (
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col span={24}>
              <Card
                size="small"
                title={
                  <Space>
                    <Text strong>Heuristic accuracy (outcome back-test)</Text>
                    <Text type="secondary" style={{ fontSize: 12, fontWeight: 'normal' }}>
                      (settled ≥{outcomeAccuracy.settle_days}d, last {outcomeAccuracy.window_days}d — label-free)
                    </Text>
                  </Space>
                }
              >
                <Row gutter={16} style={{ marginBottom: 12 }}>
                  <Col xs={12} sm={8}>
                    <Statistic
                      title="Outcome accuracy (strong classes)"
                      value={`${outcomeAccuracy.strong_accuracy_pct}%`}
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {outcomeAccuracy.strong_correct}/{outcomeAccuracy.strong_total} predictions matched node behavior
                    </Text>
                  </Col>
                  <Col xs={24} sm={16}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      'down' classes (hardware/EOL) should stay offline; 'up' classes
                      (firmware/OOR/false-alert) should recover. A high recovery rate on a
                      'down' class means those predictions were likely wrong (a broken node
                      doesn't self-heal). Ambiguous classes aren't scored.
                    </Text>
                  </Col>
                </Row>
                <Table
                  size="small"
                  pagination={false}
                  rowKey={(r) => String(r.class_id ?? 'null')}
                  columns={[
                    {
                      title: 'Class',
                      key: 'class',
                      render: (r: OutcomeAccuracyResponse['by_class'][number]) =>
                        r.class_name ?? `class ${r.class_id ?? '?'}`,
                    },
                    { title: 'Expect', dataIndex: 'expectation', key: 'exp', width: 80 },
                    { title: 'n', dataIndex: 'n', key: 'n', width: 70 },
                    {
                      title: 'Recovered %',
                      key: 'rec',
                      width: 110,
                      render: (r: OutcomeAccuracyResponse['by_class'][number]) => `${r.recovery_rate_pct}%`,
                    },
                    {
                      title: 'Consistency',
                      key: 'cons',
                      width: 120,
                      render: (r: OutcomeAccuracyResponse['by_class'][number]) => {
                        if (r.consistency_pct == null) return <Tag>n/a</Tag>;
                        const c = r.consistency_pct;
                        return <Tag color={c >= 80 ? 'green' : c >= 60 ? 'orange' : 'red'}>{c}%</Tag>;
                      },
                    },
                  ]}
                  dataSource={outcomeAccuracy.by_class}
                />
              </Card>
            </Col>
          </Row>
        )}

        {/* Triage activity: are operators actually dispositioning issues vs
            everything being silently auto-closed? */}
        {triageActivity && (
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col span={24}>
              <Card
                size="small"
                title={
                  <Space>
                    <Text strong>Operator triage activity</Text>
                    <Text type="secondary" style={{ fontSize: 12, fontWeight: 'normal' }}>
                      (closes last {triageActivity.since_days}d)
                    </Text>
                  </Space>
                }
              >
                <Row gutter={16}>
                  <Col xs={12} sm={6}>
                    <Statistic
                      title="Operators triaging?"
                      value={triageActivity.operators_active ? 'Yes' : 'No'}
                      valueStyle={{ color: triageActivity.operators_active ? '#3f8600' : '#cf1322' }}
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {triageActivity.totals.operator_pct}% operator-labeled
                    </Text>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Statistic title="Operator closes" value={triageActivity.totals.operator} />
                  </Col>
                  <Col xs={12} sm={6}>
                    <Statistic title="Auto closes" value={triageActivity.totals.auto} />
                  </Col>
                  <Col xs={12} sm={6}>
                    <Statistic title="Total closes" value={triageActivity.totals.total} />
                  </Col>
                </Row>
                {!triageActivity.operators_active && (
                  <Text type="danger" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
                    No operator dispositions in the window — everything is being auto-closed.
                    The operator-label accuracy metrics (agree-rate / holdout) are not meaningful
                    until this changes.
                  </Text>
                )}
              </Card>
            </Col>
          </Row>
        )}

        {/* Heuristic drift: how much the predicted-class mix moves week over
            week (total-variation distance). Big jumps = the heuristic's output
            distribution shifted (e.g. after a version change or signal change). */}
        {classMixTrend && classMixTrend.periods.length > 0 && (
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col span={24}>
              <Card
                size="small"
                title={
                  <Space>
                    <Text strong>Heuristic drift</Text>
                    <Text type="secondary" style={{ fontSize: 12, fontWeight: 'normal' }}>
                      (predicted-class mix, {classMixTrend.weeks}w; latest drift{' '}
                      {classMixTrend.latest_drift_pct ?? '—'}%)
                    </Text>
                  </Space>
                }
              >
                <Table
                  size="small"
                  pagination={false}
                  rowKey={(r) => r.period}
                  columns={[
                    { title: 'Week of', dataIndex: 'period', key: 'period', width: 120 },
                    { title: 'Issues', dataIndex: 'total', key: 'total', width: 90 },
                    {
                      title: 'Drift vs prev',
                      key: 'drift',
                      render: (r: ClassMixTrendResponse['periods'][number]) => {
                        if (r.drift_from_prev_pct == null) return <Tag>baseline</Tag>;
                        const d = r.drift_from_prev_pct;
                        return <Tag color={d >= 30 ? 'red' : d >= 15 ? 'orange' : 'green'}>{d}%</Tag>;
                      },
                    },
                  ]}
                  dataSource={classMixTrend.periods}
                />
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
                  Drift = total-variation distance of the predicted-class distribution vs the
                  prior week (0% = same mix, 100% = entirely different). Spikes usually mean a
                  classifier version change or a signal-source shift.
                </Text>
              </Card>
            </Col>
          </Row>
        )}

        {/* Label coverage: what share of closes carry an operator label vs
            silent auto-close. The accuracy metrics above only see operator
            labels, so a low operator % means they describe a biased slice.
            The by-class autoclose-rate flags actionable classes (battery /
            hardware / EOL) being auto-resolved without human action. */}
        {labelCoverage && (
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col span={24}>
              <Card
                size="small"
                title={
                  <Space>
                    <Text strong>Label coverage</Text>
                    <Text type="secondary" style={{ fontSize: 12, fontWeight: 'normal' }}>
                      (issue closes last {labelCoverage.since_days}d)
                    </Text>
                  </Space>
                }
              >
                <Row gutter={16} style={{ marginBottom: 12 }}>
                  <Col xs={12} sm={6}>
                    <Statistic
                      title="Operator-labeled"
                      value={`${labelCoverage.coverage.operator_pct}%`}
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {labelCoverage.coverage.operator}/{labelCoverage.coverage.total} closes
                    </Text>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Statistic
                      title="Auto-closed"
                      value={`${labelCoverage.coverage.auto_pct}%`}
                      valueStyle={
                        labelCoverage.coverage.auto_pct >= 50 ? { color: '#cf1322' } : undefined
                      }
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {labelCoverage.coverage.auto} auto · {labelCoverage.coverage.jig} jig
                    </Text>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Accuracy metrics (agree-rate, Brier, holdout) are computed over
                      operator-labeled closes only. The lower the operator %, the more
                      those numbers describe a biased minority of closes.
                    </Text>
                  </Col>
                </Row>
                <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                  Auto-close rate by class
                </Text>
                <Table
                  size="small"
                  pagination={false}
                  rowKey={(r) => String(r.class_id ?? 'null')}
                  columns={[
                    {
                      title: 'Class',
                      key: 'class',
                      render: (r: LabelCoverageResponse['by_class'][number]) =>
                        r.class_name ?? `class ${r.class_id ?? '?'}`,
                    },
                    { title: 'Closes', dataIndex: 'total', key: 'total', width: 90 },
                    { title: 'Operator', dataIndex: 'operator', key: 'operator', width: 90 },
                    { title: 'Auto', dataIndex: 'auto', key: 'auto', width: 80 },
                    {
                      title: 'Auto-close %',
                      key: 'rate',
                      width: 120,
                      render: (r: LabelCoverageResponse['by_class'][number]) => {
                        const actionable = r.class_id === 5 || r.class_id === 54 || r.class_id === 55;
                        const hot = actionable && r.autoclose_rate_pct >= 50;
                        return (
                          <Tag color={hot ? 'red' : r.autoclose_rate_pct >= 50 ? 'orange' : 'default'}>
                            {r.autoclose_rate_pct}%
                          </Tag>
                        );
                      },
                    },
                  ]}
                  dataSource={labelCoverage.by_class}
                />
              </Card>
            </Col>
          </Row>
        )}

        {/* Rule-mix observability: shipped 2026-06-09 alongside the v5.6
            classifier overhaul. Three tables let the admin verify what the
            new chain is actually producing on the fleet — rule sources,
            class distribution, and the OFFLINE_UNKNOWN sub-class residual. */}
        {ruleMix && (
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col span={24}>
              <Card
                size="small"
                title={
                  <Space>
                    <Text strong>Rule mix</Text>
                    <Text type="secondary" style={{ fontSize: 12, fontWeight: 'normal' }}>
                      (predictions last {ruleMix.window_days}d, holdout excluded)
                    </Text>
                  </Space>
                }
              >
                <Row gutter={16}>
                  <Col xs={24} md={9}>
                    <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                      By rule source
                    </Text>
                    <Table
                      size="small"
                      pagination={false}
                      rowKey={(r) => `${r.source}-${r.predicted_class_id}`}
                      columns={[
                        { title: 'Source', dataIndex: 'source', key: 'source', width: 220 },
                        {
                          title: 'Class',
                          key: 'class',
                          render: (r: RuleMixResponse['by_source'][number]) =>
                            r.ticket_class ?? `class ${r.predicted_class_id ?? '?'}`,
                        },
                        { title: 'n', dataIndex: 'n', key: 'n', width: 80, align: 'right' as const },
                      ]}
                      dataSource={ruleMix.by_source}
                      locale={{ emptyText: 'No predictions in window yet.' }}
                      scroll={{ y: 320 }}
                    />
                  </Col>
                  <Col xs={24} md={9}>
                    <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                      By class × decided-by
                    </Text>
                    <Table
                      size="small"
                      pagination={false}
                      rowKey={(r) => `${r.predicted_class_id}-${r.decided_by ?? 'null'}`}
                      columns={[
                        {
                          title: 'Class',
                          key: 'class',
                          render: (r: RuleMixResponse['by_class'][number]) =>
                            r.ticket_class ?? `class ${r.predicted_class_id ?? '?'}`,
                        },
                        {
                          title: 'Decided by',
                          dataIndex: 'decided_by',
                          key: 'decided_by',
                          width: 110,
                          render: (v: string | null) => v ?? '—',
                        },
                        { title: 'n', dataIndex: 'n', key: 'n', width: 80, align: 'right' as const },
                      ]}
                      dataSource={ruleMix.by_class}
                      locale={{ emptyText: 'No predictions in window yet.' }}
                      scroll={{ y: 320 }}
                    />
                  </Col>
                  <Col xs={24} md={6}>
                    <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                      Unknown sub-class (class 10 only)
                    </Text>
                    <Table
                      size="small"
                      pagination={false}
                      rowKey="subclass"
                      columns={[
                        {
                          title: 'Sub-class',
                          dataIndex: 'subclass',
                          key: 'subclass',
                          render: (v: string) =>
                            v === 'residual' ? (
                              <Text type="secondary">{v}</Text>
                            ) : (
                              <Text code style={{ fontSize: 11 }}>{v}</Text>
                            ),
                        },
                        { title: 'n', dataIndex: 'n', key: 'n', width: 80, align: 'right' as const },
                      ]}
                      dataSource={ruleMix.by_subclass}
                      locale={{ emptyText: 'No class-10 predictions in window.' }}
                    />
                    <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
                      'residual' = class 10 with no sub-cause tag. The headline shrinkage metric is residual ÷ total class-10.
                    </Text>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        )}
      </Spin>
    </div>
    </div>
  );
};

export default TriageAgentHealth;
